/**
 * React Query hooks for ActivityPub / Fediverse features.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { activitypubApi, type PaginationParams } from '../api/activitypub';

export const activitypubKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'activitypub'] as const,
  status: (siteSlug: string) => [...activitypubKeys.all(siteSlug), 'status'] as const,
  followers: (siteSlug: string, params?: PaginationParams) =>
    [...activitypubKeys.all(siteSlug), 'followers', params] as const,
  comments: (siteSlug: string, params?: PaginationParams) =>
    [...activitypubKeys.all(siteSlug), 'comments', params] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useActivityPubStatus(siteSlug: string) {
  return useQuery({
    queryKey: activitypubKeys.status(siteSlug),
    queryFn: () => activitypubApi.getStatus(siteSlug),
    enabled: !!siteSlug,
  });
}

export function useActivityPubFollowers(siteSlug: string, params?: PaginationParams) {
  return useQuery({
    queryKey: activitypubKeys.followers(siteSlug, params),
    queryFn: () => activitypubApi.getFollowers(siteSlug, params),
    enabled: !!siteSlug,
  });
}

export function useActivityPubComments(siteSlug: string, params?: PaginationParams) {
  return useQuery({
    queryKey: activitypubKeys.comments(siteSlug, params),
    queryFn: () => activitypubApi.getComments(siteSlug, params),
    enabled: !!siteSlug,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useEnableActivityPub(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activitypubApi.enable(siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitypubKeys.all(siteSlug) });
      queryClient.invalidateQueries({ queryKey: ['sites', siteSlug] });
    },
  });
}

export function useDisableActivityPub(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activitypubApi.disable(siteSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitypubKeys.all(siteSlug) });
      queryClient.invalidateQueries({ queryKey: ['sites', siteSlug] });
    },
  });
}

export function useApproveComment(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activitypubApi.approveComment(siteSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitypubKeys.comments(siteSlug) });
    },
  });
}

export function useDeleteComment(siteSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => activitypubApi.deleteComment(siteSlug, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activitypubKeys.comments(siteSlug) });
    },
  });
}
